<template>
  <HgModal
    v-model="isVisible"
    :closeOnBackgroundClick="false"
    data-cy="apartment-selection-modal"
    @close="handleCancel"
  >
    <ToastMessage :notificationWidth="BREAKPOINTS.funnel_container_max_width.toString() + 'px'" />
    <template #title>{{ t('apartmentSelection.title') }}</template>
    <HgSelect
      name="apartmentSelection"
      :label="t('apartmentSelection.inputLabel')"
      :options="apartmentOptions"
      :modelValue="selectedValue"
      :isInvalid="!selectedValue"
      :isDirty="shouldForceCheck"
      @update:modelValue="handleSelectChange"
    >
      <template #errorMessages>
        <span>{{ t('valuationForm.emptyRadioError') }}</span>
      </template>
    </HgSelect>
    <template #footer>
      <HgButton theme="secondary-bordered" @click="handleCancel">{{ t('button.cancel') }}</HgButton>
      <HgButton @click="handleContinue">{{ t('button.continue') }}</HgButton>
    </template>
  </HgModal>
</template>

<script setup lang="ts">
import ToastMessage from '@/components/common/ToastMessage/ToastMessage.vue';
import { usePlaceholderOption } from '@/components/property-details-page/ValuationForm/util';
import { PropertyDetails } from '@/models/queries/getPropertyDetails';
import { usePropertyValuationStore } from '@/stores/propertyValuation';
import { trackingClickEvent, trackingFeatureDisplayEvent } from '@/tracking/tracking';
import { TRACKING_ACTION, TRACKING_LABEL } from '@/tracking/types';
import { BREAKPOINTS, ValuationFlow } from '@/types';
import { HgButton, HgModal, HgSelect, HgSelectOptions } from '@smg-real-estate/domus';
import isNil from 'lodash.isnil';
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const propertyValuationStore = usePropertyValuationStore();
const { propertyDetailsData, selectedPropertyDetailIndex } = storeToRefs(propertyValuationStore);
const { t } = useI18n();
const placeholderOption = usePlaceholderOption();
const emit = defineEmits<{
  (event: 'onApartmentSelect', item: PropertyDetails): void;
}>();

const shouldForceCheck = ref<boolean>(false);
const isVisible = ref<boolean>(propertyDetailsData.value.length > 1);

onMounted(() => {
  trackingFeatureDisplayEvent(
    TRACKING_ACTION.APARTMENT_SELECTION_MODAL,
    TRACKING_LABEL.VALUATION_FUNNEL,
  );
});

watch(propertyDetailsData, (value) => {
  if (value.length > 1) {
    isVisible.value = true;
  }
});

const apartmentOptions = computed(() => {
  const tempOptions: Array<HgSelectOptions> =
    propertyDetailsData.value?.map((item: PropertyDetails, index: number) => {
      const apartmentId = item.ewid ?? index + 1 + '';
      const floor = getFloor(item.floor);
      const text: string[] = [];
      if (!isNil(item.numberOfRooms)) {
        text.push(t('common.room', item.numberOfRooms));
      }
      if (!isNil(item.livingSpace)) {
        text.push(`${item.livingSpace}m²`);
      }
      text.push(`${t('apartmentSelection.residentialid')} (EWID) ${apartmentId}`);
      return {
        text: [floor, text.join(', ')].filter((value) => !!value).join(' - '),
        value: apartmentId,
      };
    }) || [];
  tempOptions.push({
    text: t('apartmentSelection.noneOption'),
    value: 'none',
  });
  return [placeholderOption.value].concat(tempOptions);
});

const selectedValue = computed<string>(() => {
  return apartmentOptions.value?.[selectedPropertyDetailIndex.value]?.value ?? '';
});

const getFloor = (floor: number | undefined): string => {
  if (floor === undefined) {
    return '';
  } else if (floor === 0) {
    return t('apartmentSelection.groundFloorShort');
  } else {
    return `${floor}${t('apartmentSelection.floorShort')}`;
  }
};
const handleSelectChange = (value: string) => {
  shouldForceCheck.value = false;
  const selectedItemIndex = apartmentOptions.value.findIndex((option) => option.value === value);
  propertyValuationStore.setSelectedPropertyDetailIndex(selectedItemIndex);
};

const handleContinue = (): void => {
  if (!selectedValue.value) {
    shouldForceCheck.value = true;
    return;
  }
  const selectedItem: PropertyDetails =
    propertyDetailsData.value[selectedPropertyDetailIndex.value - 1] ?? {};
  emit('onApartmentSelect', selectedItem);
  trackingClickEvent(
    TRACKING_ACTION.CLICK_CONTINUE_BUTTON,
    TRACKING_LABEL.APARTMENT_SELECTION_MODAL,
  );
  isVisible.value = false;
};
const handleCancel = (): void => {
  trackingClickEvent(TRACKING_ACTION.CLICK_CANCEL_BUTTON, TRACKING_LABEL.APARTMENT_SELECTION_MODAL);
  isVisible.value = false;
  propertyValuationStore.navigateBack(ValuationFlow.PropertyDetails);
};
</script>
<style lang="scss" module>
.wrapper {
  position: relative;
}
</style>
